// Classify a reconciliation record into an ON-SITE instruction — an item that can only be fixed at the
// property (in the source or target system), not by reprocessing alone. Returns null for records that
// are not an on-site item. Each instruction names WHICH system the hotel staff must go into:
//   system: 'target'  → fix in the target system (e.g. Oracle OHIP) — config / inventory
//   system: 'source'  → fix in the source system (e.g. Opera V5)    — guest / profile data
// Instruction text uses {SOURCE} / {TARGET} tokens; the page substitutes the real marketplace names.

// The OHIP error carried on a story line: "POST /rsv/… → 400  ✗ RSV00138: There are not enough rooms".
//
// GROUPING IS BY CODE. Every distinct rejection is its own item with its own fix — a rate code that does
// not exist and a room type with no availability are not one problem, and merging them produced a single
// "POST → 400" group of 106 records that told the property nothing.
//
// The comment format changed to carry the code INLINE after the ✗; the extractor still looked for it on a
// line of its own, found nothing on every record whose comment is an HTTP story, and fell back to the
// method and status. That is the whole of the collapse.
//
// THE CODE MAY BE PREFIXED. OHIP returns o:errorCode in two shapes — bare ("OPERAWS-GEN01244") and
// namespaced ("Message.RSV01304") — and the story prints whichever it got. A pattern that only accepted
// the bare form silently dropped every namespaced one into the "PUT → 400" catch-all: 97 reservations
// under one heading that told the property nothing, when each of them says "Invalid Note Type". The
// prefix is dropped from the code so both shapes group as the same rejection.
// A REJECTION WITHOUT A CODE IS STILL A REASON. OHIP returns some errors as a detail with no
// o:errorCode, so the story line reads "PUT /rsv/… → 400  ✗ Reservation is not in a modifiable state" —
// the whole reason is on the line, and the code-only pattern threw it away and grouped the record as
// "PUT → 400". The coded form is preferred (it groups two wordings of one rejection together); the
// message is the fallback key when there is no code.
//
// Bounded to 4xx/5xx so a 2xx line carrying "✗ REJECTED [PRS/SM]" — a partial rejection on a successful
// write, which has its own classifier above — is not read as the record's failure.
function ohipErrorFromStory(comment) {
    const coded = /→\s*[45]\d{2}\s*✗\s*(?:[A-Za-z][A-Za-z0-9]*\.)?([A-Z][A-Z0-9-]{4,}):\s*([^\n]+)/g;
    let m, last = null;
    while ((m = coded.exec(comment)) !== null) last = { code: m[1], message: m[2].trim() };
    if (last) return last;

    const bare = /→\s*[45]\d{2}\s*✗\s*([^\n]+)/g;
    while ((m = bare.exec(comment)) !== null) {
        const message = m[1].trim();
        if (message) last = { code: null, message };
    }
    return last;
}

function lastHttpFailure(comment) {
    const re = /(GET|PUT|POST|PATCH|DELETE)\s+(\S+)\s+→\s+(\d{3})/g;
    let m, last = null;
    while ((m = re.exec(comment)) !== null) {
        const status = parseInt(m[3], 10);
        if (status >= 400 && status !== 404) last = { method: m[1], path: m[2], status };
    }
    return last;
}

// ON-SITE ITEMS THAT ARE NOT ERRORS.
//
// Most on-site items sit on an ERROR record, and the page fetches those wholesale. Some do not: the sync
// SUCCEEDED and still left something for the property to do — a room that could not be assigned, a
// commission record that had to be dropped so the reservation could be created at all.
//
// Those records are PROCESSED, so nothing above would ever fetch them and their classifier below would
// never run. Each one therefore needs a probe: a phrase the reconciliation comment is known to contain,
// which the page uses to pull the record in before classifying it.
//
// DECLARED HERE, BESIDE THE CLASSIFIERS, deliberately. The "room not assigned" probe lived in the page
// and the commission classifier was added without one — so it matched nothing, on a screen that gave no
// hint anything was missing. Keeping the pair together makes the omission visible at the point of
// writing the rule.
// The record statuses this report is built from.
//
// ERROR    — did not go through.
// WARNING  — went through, but with a caveat that has to be addressed at the property. Terminal and
//            successful; it is not retried, and it is precisely what this report exists to list.
//
// PROCESSED is NOT here, by definition: processed means it went through with nothing left to do. A
// record that needs someone on site is a WARNING, and anything stamping PROCESSED when there is work
// outstanding is the bug — not something for this page to compensate for by fishing PROCESSED records
// back out with text probes, which is what it used to do.
// The record statuses that can carry an on-site item.
//
//   ERROR    the record did not go through at all
//   WARNING  it went through, but something is still owed at the property
//
// PROCESSED is deliberately absent: it means there is nothing left to do, so a PROCESSED record on this
// report is a record whose status is wrong — fixed where it is stamped, not compensated for here.
export const ONSITE_STATUSES = ['ERROR', 'WARNING'];

// The errors-only variant of the report. Same classifiers, same grouping, same instructions — the only
// difference is which records are pulled. It exists because a property working through the list wants
// "what is BROKEN" separately from "what also needs a look": on a project with thousands of warnings
// the errors are unfindable, and the two are actioned by different people at different times.
export const ONSITE_STATUSES_ERRORS_ONLY = ['ERROR'];

// Returns { key, label, instruction, code, system } or null.
export function classifyOnsite(record) {
    const c = record.comment || '';

    // DID THIS RECORD FAIL?
    //
    // Some rules below describe a record that WENT THROUGH and still left something for the property —
    // a commission that had to be dropped, routing that could not be sent, a room that could not be
    // assigned. Those phrases are appended to the comment by oracle-ohip whether or not the record
    // later failed for an unrelated reason, so on a FAILED record they sit in the same comment as the
    // rejection that actually stopped it.
    //
    // Matched in order, the caveat won. On prd SFOYO that put 14 reservations under "Commission removed
    // — set it at the property" when every one of them had been rejected with RSV00138 "There are not
    // enough rooms available on Room Type level": the report told the property to open a reservation
    // that does not exist and set a commission on it, and said nothing about the availability problem
    // that was the real work. It also put them on the ERRORS-ONLY report under a heading that reads as
    // a warning, which is what made it visible.
    //
    // So a caveat rule only applies when nothing failed. The failure is the instruction; the caveat
    // becomes true again once the record is fixed and reprocessed, and the report will say so then.
    //
    // Status AND the comment, because either alone has a gap: recordStatus is authoritative but is not
    // always carried on a record handed to this function, and a comment can show a 4xx on a record the
    // pipeline still counted as terminal.
    const status = String(record.recordStatus || record.recordstatus || '').toUpperCase();
    const failed = status === 'ERROR' || !!lastHttpFailure(c);

    if (c.includes('No inventory is booked for the block')) {
        const m = c.match(/\/blocks\/(\d+)\/reservations/);
        return { key: 'block-inventory', label: 'Block has no inventory', code: m ? m[1] : null, system: 'source',
            instruction: 'This block has no rooms booked (allotment). Block inventory is managed in {SOURCE}. In {SOURCE}, add the block room allotment for the relevant dates, then re-sync the block and reprocess these reservations.' };
    }
    if (/BLK01040/.test(c) || /Inventory cannot be changed to less than sold/i.test(c)) {
        const m = c.match(/less than sold\s*:\s*(\d+)/i);
        return { key: 'block-inv-below-sold', label: 'Block inventory below rooms sold', code: m ? m[1] : null, system: 'source',
            instruction: 'The block allotment is lower than the number of rooms already sold. In {SOURCE}, increase the block room allotment to at least the sold count, then re-sync the block and reprocess.' };
    }
    if (/BLK00050/.test(c) || /No rooms available at ROOM TYPE level/i.test(c)) {
        return { key: 'block-no-rooms', label: 'No rooms at room-type level', code: null, system: 'source',
            instruction: 'The block has no rooms allotted at the room-type level. In {SOURCE}, add room-type allotment to the block, then re-sync the block and reprocess.' };
    }
    // HIL-63. Not a failure — the reservation went through. A commission record belonging to a third
    // property was dropped (it would have 403'd the whole POST), so the setting is missing at the
    // target and only the property can put it back. Matched on the phrase oracle-ohip writes in
    // reservation-sanitise.describeRemovedCommissions; keep the two in step.
    if (!failed && /Removed \d+ commission record/i.test(c)) {
        const m = c.match(/not [A-Z0-9]+\):\s*([^.]+)\./);
        return { key: 'commission-dropped', label: 'Commission removed — set it at the property', code: m ? m[1].trim() : null, system: 'target',
            instruction: 'This reservation carried a commission record belonging to a DIFFERENT property, which {TARGET} refuses outright — it was removed so the rest of the reservation could be created. Nothing is wrong with the booking. In {TARGET}, open this reservation and set the commission (code, payment method and bank account) that applies at this hotel. Do not copy the values from the other property.' };
    }
    // HIL-69. Not a failure either — the reservation went through. A routing instruction pointing at
    // another reservation's folio was removed (sent as-is, {TARGET} rejects the WHOLE reservation with
    // DPLRI), so the charges will not route until someone sets it up at the property.
    //
    // MATCHED ON THE PHRASE oracle-ohip WRITES, in reservation-sanitise.describeRemovedRoomRouting —
    // keep the two in step. And note this record's comment carries NO 4xx line: the PUT succeeded once
    // the routing was gone, so neither ohipErrorFromStory nor lastHttpFailure below can see it. Without
    // this classifier the record is WARNING and simply absent from the report.
    if (!failed && /Routing to a room was removed/i.test(c)) {
        const to = c.match(/•\s*(.+?)\s*→\s*([^(\n]+?)(?:\s*\(reservation\s*(\d+)\))?\s*$/m);
        const what = to ? to[1].trim() : null;
        const dest = to ? to[2].trim() : null;
        const resId = to && to[3] ? to[3] : null;
        return { key: 'room-routing-removed', label: 'Routing to a room removed — set it up at the property',
            code: resId || dest || null, system: 'target',
            instruction: (what && dest
                ? `This reservation routed ${what} to ${dest}${resId ? ` (reservation ${resId})` : ''}. `
                : 'This reservation routed charges to another reservation. ')
                + 'hconnect does not support routing charges between reservations, and {TARGET} rejects the '
                + 'whole reservation when one is sent — so the routing was removed and the rest of the '
                + 'reservation synced normally. Nothing is wrong with the booking. In {TARGET}, open this '
                + 'reservation and add the routing instruction by hand.' };
    }
    if (/BLK01125/.test(c) || /Please enter correct Reservation Type/i.test(c)) {
        return { key: 'block-res-type', label: 'Default reservation type not set', code: null, system: 'target',
            instruction: 'The default reservation type is not set correctly in {TARGET}. In {TARGET}, set the default reservation type on the block, then reprocess.' };
    }
    if (/RSV01320/.test(c) || /Transaction code is not configured/i.test(c)) {
        // oracle-ohip appends the offending transaction code(s) after the message: "…configured: 7010".
        const m = c.match(/Transaction code is not configured[:\s]+([A-Z0-9,\s]+)/i);
        const codes = m ? m[1].trim().replace(/\s+/g, '') : null;
        return { key: 'config-txn-code', label: 'Transaction code not configured', code: codes, system: 'target',
            instruction: codes
                ? `Transaction code ${codes} (a charge on the reservation) is not set up in {TARGET}. In {TARGET}, configure transaction code ${codes}, then reprocess.`
                : 'A transaction / rate code on the reservation is not set up in {TARGET}. In {TARGET}, configure the code, then reprocess.' };
    }
    if (/RateCodeNotFound/.test(c) || /Rate Code .* could not be found/i.test(c)) {
        const m = c.match(/Rate Code ([A-Z0-9]+) could not be found/i);
        return { key: 'rate-code-not-found', label: 'Rate code not found', code: m ? m[1] : null, system: 'target',
            instruction: 'A negotiated-rate code on the profile does not exist in {TARGET}. In {TARGET}, configure the rate code, then reprocess.' };
    }
    if (/Membership Level must be specified/i.test(c)) {
        return { key: 'membership-level', label: 'Membership level missing', code: null, system: 'source',
            instruction: 'A membership on this guest profile has no membership level in {SOURCE}. In {SOURCE}, add the membership level to the guest profile, then reprocess.' };
    }
    if (/CRM00054/.test(c) || /Invalid card number/i.test(c)) {
        return { key: 'invalid-card', label: 'Invalid card number', code: null, system: 'source',
            instruction: 'This guest profile has a payment card with an invalid number. In {SOURCE}, correct or remove the card on the profile, then reprocess.' };
    }
    if (!failed && (/not assigned in OHIP/i.test(c) || /not assigned in {TARGET}/i.test(c))) {
        const m = c.match(/Room (\S+) not assigned/i);
        return { key: 'room-not-assigned', label: 'Room not assigned', code: m ? m[1] : null, system: 'source',
            instruction: 'The reservation synced, but the pre-assigned room could not be applied in {TARGET} (it is occupied or over max occupancy). In {SOURCE}, assign a different available room to the reservation, then reprocess.' };
    }
    if (!failed && (/CRM11012/.test(c) || /Invalid Preference/i.test(c))) {
        const codes = [...c.matchAll(/rejected by OHIP:\s*([^—(\n]+)/g)].map(x => x[1].trim()).join(', ');
        return { key: 'invalid-preference', label: 'Preference code not configured', code: codes || null, system: 'target',
            instruction: 'One or more guest preference codes are not set up in {TARGET}, so those preferences were not applied (the guest profile itself synced). In {TARGET}, configure the preference code(s) for the preferences to apply.' };
    }
    // RSV01304. ONE note with a type the target has not configured rejects the WHOLE reservation, and
    // OHIP's message ("Invalid Note Type") names neither the note nor the type — so oracle-ohip appends
    // the types the call carried (see the RSV01304 enrichment in local.js; keep the two in step). Named
    // here rather than left to the generic code classifier because the fix is a specific piece of
    // configuration, and because "which note type?" is the only question the property will ask.
    if (/RSV01304/.test(c) || /Invalid Note Type/i.test(c)) {
        // Bounded to the line: \s matches a newline, so a greedy class ran on into the next story line
        // and made "RESERVATION, GUEST_REQUEST" read as "RESERVATION, GUEST_REQUEST GET".
        const m = c.match(/Invalid Note Type[: \t]+([A-Z0-9_][A-Z0-9_, \t]*)/i);
        const types = m ? m[1].trim().replace(/\s+/g, ' ').replace(/[,\s]+$/, '') : null;
        return { key: 'invalid-note-type', label: types ? `Note type not configured — ${types}` : 'Note type not configured',
            code: types, system: 'target',
            instruction: types
                ? `The reservation carries notes of type ${types}, and at least one of those types is not configured in {TARGET} — {TARGET} rejects the whole reservation, not just the note. In {TARGET}, add the missing reservation note type(s) for this property, then reprocess.`
                : 'A note on this reservation has a type that is not configured in {TARGET}, and {TARGET} rejects the whole reservation rather than just the note. In {TARGET}, add the missing reservation note type for this property, then reprocess.' };
    }
    if (/RSV01636/.test(c) || /Unable to update guarantee code for a In House reservation/i.test(c)) {
        return { key: 'guarantee-inhouse', label: 'Guarantee code can’t be updated (In House)', code: null, system: 'target',
            instruction: 'This reservation is already In House (checked in) in {TARGET}, so {TARGET} will not change its guarantee code. If the guarantee needs updating, change it at the front desk in {TARGET}; otherwise no action is needed.' };
    }
    // The specific OHIP rejection, keyed on its code so each one is its own group — or on the message
    // itself when OHIP sent no code, which is still incomparably better than the method and status.
    const ohip = ohipErrorFromStory(c);
    if (ohip) {
        // KEYED ON THE MESSAGE WITH ITS VARIABLES REMOVED. A codeless message usually names the record's
        // own values — "no more rooms available for room type Q1RP on 2026-10-19" — so keying on the raw
        // text splits one problem into a group per room type and date, which is the collapse this file
        // exists to prevent, inverted. Dates, numbers and code-shaped tokens are blanked for the key
        // only; the label keeps the real message, because a property needs to see one.
        const varied = ohip.message
            .replace(/\d{4}-\d{2}-\d{2}/g, '#')
            .replace(/\b\d+(?:[.,]\d+)?\b/g, '#')
            .replace(/\b[A-Z0-9]{2,10}\b/g, '#');
        const slug = varied.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48).replace(/^-+|-+$/g, '');
        const perRecord = varied !== ohip.message;
        return { key: ohip.code ? `ohip-${ohip.code}` : `ohip-msg-${slug}`,
            label: ohip.code ? `${ohip.code} — ${ohip.message.slice(0, 70)}` : ohip.message.slice(0, 80),
            code: ohip.code, system: 'target',
            // This record's own message, and the shape the group shares — see the server copy in
            // /opt/core/onsite-instructions.js. summariseGroupValues() below turns these into the list of
            // real values and a heading that does not quote one record over the rest (HIL-90).
            detail: ohip.message,
            pattern: ohip.code ? `${ohip.code} — ${varied}` : varied,
            instruction: `${ohip.message}${/[.!?]$/.test(ohip.message) ? '' : '.'} `
                + 'Correct this in {TARGET} (or in {SOURCE} if the data originates there), then reprocess '
                + (perRecord
                    ? 'the records listed below. They failed for the same reason, but each names its own '
                        + 'value — the values are listed with the number of records carrying each.'
                    : 'the records listed below. They all failed for the same reason.') };
    }

    // MISSING LINKED RECORD. The pipeline refuses to send a record whose linked profile/reservation/block
    // is not on the target yet, and writes "Missing linked <Kind> <id>" (see the rule in CLAUDE.md). It
    // carries no HTTP line, so nothing below sees it and the record used to vanish from this report
    // entirely — while the Reconciliation screen offers to request the missing record with one click.
    const missing = c.match(/Missing linked\s+(\w+)\s+(\S+)/i);
    if (missing) {
        return { key: `missing-linked-${missing[1].toLowerCase()}`,
            label: `Linked ${missing[1].toLowerCase()} not synced yet`, code: null, system: null,
            instruction: `These records reference a ${missing[1].toLowerCase()} that has not reached {TARGET} yet, so they `
                + 'were not sent — sending them would silently lose the link. No change is needed at the '
                + 'property: request the missing record(s) from the Reconciliation screen (it offers this '
                + 'for exactly these comments), let them sync, then reprocess these.' };
    }

    const fail = lastHttpFailure(c);
    if (fail) {
        // Surface the SPECIFIC OHIP error — per R5 the comment leads with the error detail (the first line
        // that isn't an HTTP "…→ 400" story line). A generic "review the message log" is not actionable.
        const detail = (c.split('\n').map(l => l.trim()).find(l => l && !/→\s*\d{3}/.test(l) && !/^(GET|PUT|POST|PATCH|DELETE)\b/.test(l)) || '').replace(/\s+/g, ' ').trim();
        return { key: detail ? `review-${detail.slice(0, 40).toLowerCase()}` : 'review', label: detail ? detail.slice(0, 80) : `${fail.method} → ${fail.status}`, code: null, system: 'target',
            instruction: detail
                ? `${detail}${/[.!?]$/.test(detail) ? '' : '.'} Correct the underlying data or configuration in {TARGET} (or {SOURCE}), then reprocess. See the record’s message log for detail.`
                : '{TARGET} rejected this record. Review the record’s message log and correct the underlying data or configuration in {TARGET} or {SOURCE}, then reprocess.' };
    }
    // FINAL FALLBACK — A FAILURE WITH NO RULE IS STILL A FAILURE.
    //
    // Everything above matches a known shape. Anything else used to return null and DISAPPEAR: on prd
    // project 335 that was 163 of 164 failed profile records, including every "OHIP connector 3116 is
    // not configured" — so the report said the profiles were fine while none of them had synced, and
    // COMPANY was absent from the report altogether. A report whose omissions are invisible is worse
    // than one that admits it does not know: the reader has no way to tell "nothing wrong" from
    // "nothing recognised".
    //
    // Grouped on the first line with its variables blanked, so one cause is one heading rather than one
    // per record. `system: null` because we genuinely do not know which side it belongs to — and saying
    // "fix this in {TARGET}" when it is our own configuration would send somebody to the wrong system.
    const firstLine = c.split('\n').map(l => l.trim()).find(Boolean);
    if (firstLine) {
        const varied = firstLine
            .replace(/\d{4}-\d{2}-\d{2}/g, '#')
            .replace(/\b\d+(?:[.,]\d+)?\b/g, '#')
            .replace(/\b[A-Z0-9]{2,10}\b/g, '#');
        const slug = varied.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48).replace(/^-+|-+$/g, '');
        return { key: `unclassified-${slug}`, label: firstLine.slice(0, 80), code: null, system: null,
            unclassified: true,
            instruction: `${firstLine}${/[.!?]$/.test(firstLine) ? '' : '.'} `
                + 'These records did not sync and there is no standard on-site fix for this yet. Check one '
                + 'record\u2019s message log to see which side it belongs to, correct it there, then reprocess. '
                + 'If this turns out to be a common cause, it is worth a rule of its own so the instruction '
                + 'can say exactly what to do.' };
    }

    return null;
}

// Fill {SOURCE}/{TARGET} tokens with the real system names for display.
export function fillSystems(text, sourceName, targetName) {
    return String(text || '')
        .replace(/\{SOURCE\}/g, sourceName || 'the source system')
        .replace(/\{TARGET\}/g, targetName || 'the target system');
}

// ── WHAT THE RECORDS IN A GROUP ACTUALLY NAME ───────────────────────────────────────────────────────
//
// The mirror of summariseValues() in /opt/core/onsite-instructions.js, and it must stay the same: the
// screen classifies client-side while the emailed and printed report is built on the server, and a
// property comparing the two has no way to know which one to believe.
//
// A group is one problem with one instruction — 151 rate-code rejections are one job at the property.
// They are not one VALUE, and the heading used to claim they were, taking it from whichever record was
// classified first (HIL-90).
//
// Mutates group.meta in place and returns the group, so a caller can map over its groups.
export function summariseGroupValues(group, detailOf) {
    const meta = group && group.meta;
    if (!meta) return group;
    const counts = new Map();
    for (const rec of (group.records || [])) {
        const value = detailOf ? detailOf(rec) : (rec && (rec.detail || rec._g?.detail || rec._g?.code));
        if (!value) continue;
        counts.set(value, (counts.get(value) || 0) + 1);
    }
    if (!counts.size) return group;
    meta.values = [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => b.count - a.count || String(a.value).localeCompare(String(b.value)));
    meta.varies = counts.size > 1;
    if (meta.varies && meta.pattern) {
        meta.sampleLabel = meta.label;
        meta.label = meta.pattern.replace(/#/g, '\u2026').slice(0, 80);
        const generalMessage = meta.pattern.replace(/^\S+\s+\u2014\s+/, '').replace(/#/g, '\u2026');
        if (meta.detail && meta.instruction && meta.instruction.startsWith(meta.detail)) {
            meta.instruction = generalMessage + meta.instruction.slice(meta.detail.length);
        }
    }
    return group;
}
